<script setup>
import { ref, watch } from 'vue'
import LoaderIcon from '@/components/utils/LoaderIcon.vue'

const props = defineProps({
  supportedLanguages: {
    type: Array,
    required: true
  },
  supportedScenarios: {
    type: Array,
    required: true
  },
  supportedDifficulties: {
    type: Array,
    required: true
  },
  isOpen: {
    type: Boolean,
    required: true
  },
  isCreating: {
    type: Boolean,
    default: false
  },
  errorCreating: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['create', 'close'])

const selectedLanguage = ref('Select language')
const selectedScenario = ref('Select scenario')
const selectedDifficulty = ref('Select difficulty')
const errorMessage = ref('')

// Validation state
const validationState = ref({
  language: true,
  scenario: true,
  difficulty: true
})

watch(selectedLanguage, (newValue) => {
  if (newValue) validationState.value.language = true
})

watch(selectedScenario, (newValue) => {
  if (newValue) validationState.value.scenario = true
})

watch(selectedDifficulty, (newValue) => {
  if (newValue) validationState.value.difficulty = true
})

watch(
  () => [props.isOpen, props.errorCreating],
  ([newIsOpen, newErrorCreating], [oldIsOpen, oldErrorCreating]) => {
    if (newIsOpen && !oldIsOpen && !newErrorCreating) {
      console.log('Modal opened')
      resetForm()
    }
    if (newErrorCreating) {
      console.error('Error creating conversation:', newErrorCreating)
      errorMessage.value = newErrorCreating
    }
  }
)

function resetForm () {
  errorMessage.value = ''
  selectedLanguage.value = 'Select language'
  selectedScenario.value = 'Select scenario'
  selectedDifficulty.value = 'Select difficulty'
  validationState.value = {
    language: true,
    scenario: true,
    difficulty: true
  }
}

async function handleSubmit (event) {
  event.preventDefault()
  if (props.isCreating) return
  errorMessage.value = ''

  // Validate form fields
  validationState.value.language = selectedLanguage.value && selectedLanguage.value !== 'Select language'
  validationState.value.scenario = selectedScenario.value && selectedScenario.value !== 'Select scenario'
  validationState.value.difficulty = selectedDifficulty.value && selectedDifficulty.value !== 'Select difficulty'

  if (!validationState.value.language || !validationState.value.scenario || !validationState.value.difficulty) {
    errorMessage.value = 'Please fill in all fields.'
    return
  }

  try {
    emit('create', {
      language: selectedLanguage.value,
      scenario: selectedScenario.value,
      difficulty: selectedDifficulty.value
    })
  } catch (error) {
    console.error('Error creating conversation:', error)
    errorMessage.value = 'There was an issue when creating a new scenario. Please try again.'
  }
}

function handleClose () {
  resetForm()
  emit('close')
}
</script>

<template>
  <div v-if="isOpen" class="fixed inset-0 z-50 overflow-y-auto">
    <!-- Backdrop -->
    <div class="fixed inset-0 bg-gray-900 bg-opacity-50 transition-opacity"></div>

    <!-- Modal -->
    <div class="flex min-h-full items-center justify-center p-4 text-center">
      <div class="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
        <!-- Modal header -->
        <div class="flex items-center justify-between p-4 md:p-5 border-b rounded-t border-gray-200">
          <h3 class="text-lg font-semibold text-gray-900">
            {{ $t('conversationsSidebar.newScenarioTitle') }}
          </h3>
          <!-- Close modal button -->
          <button type="button"
            class="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center"
            @click="handleClose">
            <svg class="w-3 h-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none"
              viewBox="0 0 14 14">
              <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6" />
            </svg>
            <span class="sr-only">{{ $t('conversationsSidebar.closeModal') }}</span>
          </button>
        </div>
        <!-- Modal body -->
        <form class="p-4 md:p-5" @submit="handleSubmit">
          <div v-if="errorMessage" class="mb-4 p-3 text-red-700 bg-red-100 border border-red-400 rounded-lg text-sm">
            {{ errorMessage }}
          </div>
          <div class="grid gap-4 mb-4 grid-cols-1">
            <!-- Language -->
            <div class="col-span-2 sm:col-span-1">
              <label :for="language"
                :class="['block mb-2 text-sm font-medium', validationState.language ? 'text-gray-900' : 'text-red-700']">
                {{ $t('conversationsSidebar.languageLabel') }}
              </label>
              <select id="language" v-model="selectedLanguage"
                :class="['bg-gray-50 border text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block w-full p-2.5', validationState.language ? 'border-gray-300 text-gray-900' : 'bg-red-50 border-red-500 text-red-900 placeholder-red-700 focus:ring-red-500 focus:border-red-500']">
                <option selected="" disabled>{{ $t('conversationsSidebar.languagePlaceholder') }}</option>
                <option v-for="language in props.supportedLanguages" :key="language" :value="language">
                  {{ language }}
                </option>
              </select>
              <p v-if="!validationState.language" class="mt-2 text-sm text-red-600">
                {{ $t('conversationsSidebar.languageError') }}
              </p>
            </div>
            <!-- Scenario -->
            <div class="col-span-2 sm:col-span-1">
              <label :for="scenario"
                :class="['block mb-2 text-sm font-medium', validationState.scenario ? 'text-gray-900' : 'text-red-700']">
                {{ $t('conversationsSidebar.scenarioLabel') }}
              </label>
              <select id="scenario" v-model="selectedScenario"
                :class="['bg-gray-50 border text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block w-full p-2.5', validationState.scenario ? 'border-gray-300 text-gray-900' : 'bg-red-50 border-red-500 text-red-900 placeholder-red-700 focus:ring-red-500 focus:border-red-500']">
                <option selected="" disabled>{{ $t('conversationsSidebar.scenarioPlaceholder') }}</option>
                <option v-for="scenario in props.supportedScenarios" :key="scenario" :value="scenario">
                  {{ scenario }}
                </option>
              </select>
              <p v-if="!validationState.scenario" class="mt-2 text-sm text-red-600">
                {{ $t('conversationsSidebar.scenarioError') }}
              </p>
            </div>
            <!-- Difficulty -->
            <div class="col-span-2 sm:col-span-1">
              <label :for="difficulty"
                :class="['block mb-2 text-sm font-medium', validationState.difficulty ? 'text-gray-900' : 'text-red-700']">
                {{ $t('conversationsSidebar.difficultyLabel') }}
              </label>
              <select id="difficulty" v-model="selectedDifficulty"
                :class="['bg-gray-50 border text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block w-full p-2.5', validationState.difficulty ? 'border-gray-300 text-gray-900' : 'bg-red-50 border-red-500 text-red-900 placeholder-red-700 focus:ring-red-500 focus:border-red-500']">
                <option selected="" disabled>{{ $t('conversationsSidebar.difficultyPlaceholder') }}</option>
                <option v-for="difficulty in props.supportedDifficulties" :key="difficulty" :value="difficulty">
                  {{ difficulty }}
                </option>
              </select>
              <p v-if="!validationState.difficulty" class="mt-2 text-sm text-red-600">
                {{ $t('conversationsSidebar.difficultyError') }}
              </p>
            </div>
          </div>
          <!-- Submit button -->
          <button type="submit" :disabled="props.isCreating"
            class="text-white inline-flex items-center bg-paletteDarkGreen hover:bg-paletteLightGreen focus:ring-4 focus:outline-none focus:ring-gray-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center w-full justify-center disabled:bg-gray-500 disabled:cursor-not-allowed">
            <span v-if="props.isCreating" class="flex items-center">
              <LoaderIcon />
              {{ $t('conversationsSidebar.creating') }}
            </span>
            <span v-else>{{ $t('conversationsSidebar.startConversation') }}</span>
          </button>
        </form>
      </div>
    </div>
  </div>
</template>
